import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { startBackgroundPreload } from '../lib/backgroundPreloader';

const configuredPassword = import.meta.env.VITE_SITE_PASSWORD || 'preview-access';

const PasswordProtection = ({ children, migrationDone = true }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Only check authentication state after storage migration is complete
    if (!migrationDone) {
      return;
    }

    // Complete auth bypass in development mode
    if (import.meta.env.DEV && import.meta.env.VITE_DISABLE_AUTH_IN_DEV === 'true') {
      console.log('🔓 Dev mode: Site password disabled - auto-authenticating');
      setIsAuthenticated(true);
      return;
    }

    // Check if already authenticated
    try {
      const authenticated = localStorage.getItem('pm_authenticated');
      if (authenticated === 'true') {
        setIsAuthenticated(true);
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('Unable to access localStorage:', err);
      }
      // Continue without authentication state
    }

    // Start background preloading immediately when password page loads
    // This ensures data is ready by the time users authenticate and start using the app
    startBackgroundPreload();
  }, [migrationDone]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === configuredPassword) {
      setIsAuthenticated(true);
      try {
        localStorage.setItem('pm_authenticated', 'true');
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('Unable to save authentication state:', err);
        }
        // Continue without saving authentication state
      }
      setError('');
    } else {
      setError('Incorrect password');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-xl sm:text-2xl">SO LOOKUP</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="Enter password"
                  aria-label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 text-base"
                />
                {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
              </div>
              <Button type="submit" className="w-full min-h-[44px] text-base">
                Access Application
              </Button>
              {!import.meta.env.VITE_SITE_PASSWORD && (
                <p className="text-sm text-muted-foreground mt-2 text-center">
                  Preview environment: Use password &quot;{configuredPassword}&quot;
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return children;
};

export default PasswordProtection;